import React, { useState, useMemo, useRef, useEffect } from 'react';
import FileItem from './FileItem';
import './BriefcasePanel.css';

/**
 * BriefcasePanel component for displaying and managing uploaded files
 * This implements the Briefcase feature as a workflow tab that allows users to:
 * 1. Access uploaded files and processed outputs in a right-sided workflow tab
 * 2. View files organized by workflow steps (Campaign, Creative, Launch Configuration)
 * 3. Perform actions via context menu (reference in chat, navigate to message)
 * 4. Rename files for easier recall
 * 5. Pin important files for quick access
 * 6. Collapse/expand workflow sections
 */
const BriefcasePanel = ({
  files,
  onNavigateToMessage,
  onReferenceInChat,
  onRenameFile,
  onTogglePin
}) => {
  const [activeTab, setActiveTab] = useState('workflow');
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [briefcaseWidth, setBriefcaseWidth] = useState(() => {
    // Default width is 300px
    const width = 300;

    // Set the CSS variable immediately during initialization
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--briefcase-width', `${width}px`);
    }

    return width;
  });

  // Refs for resize functionality
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(briefcaseWidth);

  // Handle resize start
  const handleResizeStart = (e) => {
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = briefcaseWidth;

    // Add the event listeners to the window for better tracking
    window.addEventListener('mousemove', handleResize, { passive: true });
    window.addEventListener('mouseup', handleResizeEnd);

    // Add a resize class to the body to disable text selection and set cursor
    document.body.classList.add('resizing');
    document.body.classList.add('resizing-briefcase');
    document.body.style.cursor = 'ew-resize';
  };

  const handleResize = (e) => {
    if (!isResizingRef.current) return;

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Match the sidebar implementation for consistent behavior
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.max(250, Math.min(window.innerWidth * 0.3, startWidthRef.current - delta));

      // Only update if the width has changed by at least 1px
      if (Math.abs(newWidth - briefcaseWidth) >= 1) {
        setBriefcaseWidth(newWidth);
        // Update the CSS variable for briefcase width
        document.documentElement.style.setProperty('--briefcase-width', `${newWidth}px`);
      }
    });
  };

  const handleResizeEnd = () => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);

    // Remove the resize class
    document.body.classList.remove('resizing');
    document.body.classList.remove('resizing-briefcase');
    document.body.style.cursor = '';
  };

  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      if (isResizingRef.current) {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-briefcase');
        document.body.style.cursor = '';
      }
    };
  }, []);

  // Define workflow sections and their order (descending, latest at top)
  const workflowSections = [
    { id: 'campaign', name: 'Campaign', icon: 'ads_click' },
    { id: 'launch', name: 'Launch Configuration', icon: 'rocket_launch' },
    { id: 'creative', name: 'Creative', icon: 'brush' },
    { id: 'io', name: 'IO', icon: 'receipt_long' }
  ];

  // Toggle section collapse state
  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Filter and sort files based on search term
  const filteredFiles = useMemo(() => {
    // Skip invalid files and filter by search term
    return files.filter(file => {
      if (!file || !file.name) return false;
      return searchTerm === '' || file.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [files, searchTerm]);

  // Get uploaded files sorted by timestamp (newest first), with pinned files at the top
  const uploadedFiles = useMemo(() => {
    return [...filteredFiles]
      .filter(file => file.type === 'uploaded')
      .sort((a, b) => {
        // First sort by pinned status (pinned files first)
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        // Then sort by timestamp (newest first)
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
  }, [filteredFiles]);

  // Get generated files grouped by workflow section
  const workflowFiles = useMemo(() => {
    // Filter for generated files only
    const generatedFiles = filteredFiles.filter(file => file.type === 'generated');

    // Sort by pinned status first, then by timestamp (descending - newest first)
    const sortedFiles = [...generatedFiles].sort((a, b) => {
      // First sort by pinned status (pinned files first)
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      // Then sort by timestamp (newest first)
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    // Group files by section
    const result = {};

    // Initialize all sections
    workflowSections.forEach(section => {
      result[section.id] = [];
    });

    // Assign files to sections based on their metadata
    sortedFiles.forEach(file => {
      // Get the action or name to categorize by
      const actionOrName = (file.action || file.name || '').toLowerCase();

      // Categorize based on specific keywords
      if (actionOrName.includes('campaign-processor') || actionOrName.includes('campaign-augmentor')) {
        result.io.push(file);
      } else if (actionOrName.includes('creative-processor') || actionOrName.includes('creative-augmentor')) {
        result.creative.push(file);
      } else if (actionOrName.includes('campaign-launcher') || actionOrName.includes('launch-augmentor')) {
        result.launch.push(file);
      } else if (actionOrName.includes('fb-api') || actionOrName.includes('tiktok-api') || actionOrName.includes('dv360-api') || actionOrName.includes('ttd-api') || actionOrName.includes('gam-api') || actionOrName.includes('g-ads-api') || actionOrName.includes('fast-track')) {
        result.campaign.push(file);
      } else {
        // Default to campaign if no match
        result.campaign.push(file);
      }
    });

    return result;
  }, [filteredFiles, workflowSections]);

  // Count files by type
  const uploadedCount = useMemo(() =>
    uploadedFiles.length, [uploadedFiles]);

  const generatedCount = useMemo(() =>
    filteredFiles.filter(file => file.type === 'generated').length, [filteredFiles]);

  // Use a ref to track initialization and prevent infinite loops
  const hasInitializedRef = useRef(false);
  const prevWorkflowFilesRef = useRef(null);

  // Handle collapsed sections initialization and updates
  useEffect(() => {
    // Skip if workflowFiles isn't available yet
    if (!workflowFiles) return;

    // Check if workflowFiles has actually changed to prevent unnecessary updates
    const workflowFilesJSON = JSON.stringify(workflowFiles);
    if (prevWorkflowFilesRef.current === workflowFilesJSON && hasInitializedRef.current) {
      return;
    }

    // Update our ref with the current workflowFiles
    prevWorkflowFilesRef.current = workflowFilesJSON;

    // If this is the first time, initialize all sections
    if (!hasInitializedRef.current) {
      const initialState = {};

      // Set initial collapsed state based on whether sections have files
      workflowSections.forEach(section => {
        const sectionFiles = workflowFiles[section.id] || [];
        initialState[section.id] = sectionFiles.length === 0;
      });

      setCollapsedSections(initialState);
      hasInitializedRef.current = true;
    } else {
      // For subsequent updates, only ensure empty sections are collapsed
      // but don't change user-expanded sections
      setCollapsedSections(prevState => {
        let newState = {...prevState};
        let hasChanges = false;

        // Only collapse sections that have become empty
        workflowSections.forEach(section => {
          const sectionFiles = workflowFiles[section.id] || [];
          if (sectionFiles.length === 0 && !newState[section.id]) {
            newState[section.id] = true;
            hasChanges = true;
          }
        });

        // Only return a new object if something changed
        return hasChanges ? newState : prevState;
      });
    }
  }, [workflowFiles, workflowSections]);



  return (
    <div className="workflow-tab briefcase-tab">
      <div className="workflow-tab-header">
        <div className="workflow-tab-title">
          <span className="material-symbols-outlined">work</span>
          <h3>Briefcase</h3>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          )}
        </div>

        {/* Tab navigation */}
        <div className="briefcase-tabs">
          <button
            className={`briefcase-tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('workflow')}
          >
            <span className="material-symbols-outlined">category</span>
            Workflow
            <span className="tab-badge">{generatedCount}</span>
          </button>
          <button
            className={`briefcase-tab-button ${activeTab === 'uploaded' ? 'active' : ''}`}
            onClick={() => setActiveTab('uploaded')}
          >
            <span className="material-symbols-outlined">upload_file</span>
            Uploaded
            <span className="tab-badge">{uploadedCount}</span>
          </button>
        </div>

        <div className="workflow-tab-subtitle">
          {activeTab === 'workflow'
            ? 'Generated files grouped by workflow step.'
            : 'All uploaded files in chronological order.'}
        </div>
      </div>

      <div className="workflow-tab-content">
        {/* Workflow Tab - Display workflow sections */}
        {activeTab === 'workflow' && (
          <>
            {/* Display workflow sections in descending order (latest at top) */}
            {workflowSections.map(section => {
              const sectionFiles = workflowFiles[section.id] || [];
              const isCollapsed = collapsedSections[section.id];
              const hasFiles = sectionFiles.length > 0;

              // Count approved files in this section
              const approvedFilesCount = sectionFiles.filter(file =>
                file.type === 'generated' && file.status === 'approved'
              ).length;

              return (
                <div key={section.id} className="workflow-section">
                  <div className="workflow-section-header">
                    <button
                      className={`workflow-section-toggle ${hasFiles ? 'has-files' : ''}`}
                      onClick={() => toggleSectionCollapse(section.id)}
                    >
                      <div className="section-toggle-content">
                        <div className="section-icon-wrapper">
                          <span className="material-symbols-outlined">{section.icon}</span>
                          {approvedFilesCount > 0 && (
                            <span className="section-approve-indicator material-symbols-outlined">
                              verified
                            </span>
                          )}
                        </div>
                        <span className="section-name">{section.name}</span>
                        <span className="tab-badge">{sectionFiles.length}</span>
                      </div>
                      <span className={`material-symbols-outlined collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
                        expand_more
                      </span>
                    </button>
                  </div>

                  {!isCollapsed && (
                    <div className="workflow-section-content">
                      {hasFiles ? (
                        <div className="files-list">
                          {sectionFiles.map(file => (
                            <FileItem
                              key={file.id}
                              file={file}
                              onNavigateToMessage={onNavigateToMessage}
                              onReferenceInChat={onReferenceInChat}
                              onRename={onRenameFile}
                              onTogglePin={onTogglePin}
                              showAction={false}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="empty-files">
                          <span className="material-symbols-outlined">
                            {searchTerm ? 'search_off' : 'folder_off'}
                          </span>
                          <p>
                            {searchTerm
                              ? 'No matching files found'
                              : `No files in ${section.name}`}
                          </p>
                          <div className="empty-description">
                            {searchTerm
                              ? 'Try adjusting your search term'
                              : `Files from ${section.name} will appear here`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Show empty state if no generated files match search */}
            {(searchTerm && Object.values(workflowFiles).every(files => files.length === 0)) && (
              <div className="empty-workflow-tab">
                <p>No matching generated files found</p>
                <div className="empty-description">
                  Try adjusting your search term
                </div>
              </div>
            )}

            {/* Show empty state if no generated files at all */}
            {(!searchTerm && generatedCount === 0) && (
              <div className="empty-workflow-tab">
                <p>No generated files</p>
                <div className="empty-description">
                  Generated files will appear here
                </div>
              </div>
            )}
          </>
        )}

        {/* Uploaded Tab - Display all uploaded files */}
        {activeTab === 'uploaded' && (
          <div className="uploaded-files-container">
            {uploadedFiles.length > 0 ? (
              <div className="files-list">
                {uploadedFiles.map(file => (
                  <FileItem
                    key={file.id}
                    file={file}
                    onNavigateToMessage={onNavigateToMessage}
                    onReferenceInChat={onReferenceInChat}
                    onRename={onRenameFile}
                    onTogglePin={onTogglePin}
                    showAction={true}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-workflow-tab">
                <span className="material-symbols-outlined">
                  {searchTerm ? 'search_off' : 'upload_file'}
                </span>
                <p>
                  {searchTerm
                    ? 'No matching uploaded files found'
                    : 'No uploaded files'}
                </p>
                <div className="empty-description">
                  {searchTerm
                    ? 'Try adjusting your search term'
                    : 'Upload files to see them here'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resize handle */}
      <div
        className="workflow-tab-resize-handle"
        onMouseDown={handleResizeStart}
      />
    </div>
  );
};

export default BriefcasePanel;
